import { Injectable, inject } from '@angular/core';
import { BsCacheService, AwsApiWrapperService } from '@brightside-web/desktop/data-access/core-services';
import { from, Observable, of, pipe, throwError } from 'rxjs';
import { catchError, retryWhen, take, delay, concatMap, tap } from 'rxjs/operators';
import * as Sentry from '@sentry/angular';
import type { Scope } from '@sentry/core';
import moment from 'moment';
import { FirebaseService } from '@brightside-web/desktop/data-access/shared';
import { Environment } from '@micro-core/environment';
import { HttpErrorResponse } from '@angular/common/http';

export interface GenericResponseResult {
  result: GenericResponseResultItems;
}

export interface GenericResponseResultItems {
  status: number;
  code: string;
  message: string;
  suggestion: string;
  suggestionCopyKey: string;
  docs: string;
}

export enum GenericResponseCode {
  SERVICE_OK = 'SERVICE_OK',
  MISSING_REQ_FIELD = 'MISSING_REQ_FIELD',
  INVALID_FIELD = 'INVALID_FIELD',
  SERVICE_ERROR = 'SERVICE_ERROR',
}

export interface APIOptionsInterface {
  retryCount?: number;
  cache?: boolean;
  errorCallback?(error: unknown): unknown;
}

abstract class APIWrapperClass {
  simulateError = false;

  defaultRetryCount = 3;

  constructor(protected amplitude: FirebaseService, protected env: Environment, protected awsAPIWrapper: AwsApiWrapperService) {}

  private _buildInitBody(init: any, traceId: string): { body: any, headers: { 'X-BS-Trace-Id': string } } {
    const initBody: any = {};

    if (init) {
      initBody.body = { ...init };
    }

    initBody.headers = {
      ...(initBody.headers || {}),
      'X-BS-Trace-Id': traceId,
    };

    return initBody;
  }

  protected _apiGet<T>(
    apiName: string,
    path: string,
    init: any,
    options?: APIOptionsInterface
  ): Observable<T & GenericResponseResult> {
    const traceId = crypto.randomUUID();
    const initBody = this._buildInitBody(init, traceId);

    if (!this.env.production) {
      this.amplitude.logEvent(
        'api call started',
        {
          body: JSON.stringify(initBody.body),
          type: 'get',
          url: `${this.env.awsmobile.endpoints['api-mobile'].endpoint}${path}`,
          trace_id: traceId,
        },
        true,
        100
      );
    }

    return from(this.awsAPIWrapper.get(apiName, path, initBody)).pipe(this._apiPipe<T>(apiName, path, initBody, 'get', options, traceId));
  }

  protected _apiPost<T>(
    apiName: string,
    path: string,
    init: any,
    options?: APIOptionsInterface
  ): Observable<T & GenericResponseResult> {
    const traceId = crypto.randomUUID();
    const initBody = this._buildInitBody(init, traceId);

    if (!this.env.production) {
      this.amplitude.logEvent(
        'api call started',
        {
          body: JSON.stringify(initBody.body),
          type: 'post',
          url: `${this.env.awsmobile.endpoints['api-mobile'].endpoint}${path}`,
          trace_id: traceId,
        },
        true,
        100
      );
    }
    return from(this.awsAPIWrapper.post(apiName, path, initBody)).pipe(this._apiPipe<T>(apiName, path, initBody, 'post', options, traceId));
  }

  protected _apiPatch<T>(
    apiName: string,
    path: string,
    init: any,
    options?: APIOptionsInterface
  ): Observable<T & GenericResponseResult> {
    const traceId = crypto.randomUUID();
    const initBody = this._buildInitBody(init, traceId);
    if (!this.env.production) {
      this.amplitude.logEvent(
        'api call started',
        {
          body: JSON.stringify(initBody.body),
          type: 'patch',
          url: `${this.env.awsmobile.endpoints['api-mobile'].endpoint}${path}`,
          trace_id: traceId,
        },
        true,
        100
      );
    }
    return from(this.awsAPIWrapper.patch(apiName, path, initBody)).pipe(this._apiPipe<T>(apiName, path, initBody, 'patch', options, traceId));
  }

  protected _apiPut<T>(
    apiName: string,
    path: string,
    init: any,
    options?: APIOptionsInterface
  ): Observable<T & GenericResponseResult> {
    const traceId = crypto.randomUUID();
    const initBody = this._buildInitBody(init, traceId);
    if (!this.env.production) {
      this.amplitude.logEvent(
        'api call started',
        {
          body: JSON.stringify(initBody.body),
          type: 'put',
          url: `${this.env.awsmobile.endpoints['api-mobile'].endpoint}${path}`,
          trace_id: traceId,
        },
        true,
        100
      );
    }
    return from(this.awsAPIWrapper.put(apiName, path, initBody)).pipe(this._apiPipe<T>(apiName, path, initBody, 'put', options, traceId));
  }

  protected _apiDel<T>(
    apiName: string,
    path: string,
    init: any,
    options?: APIOptionsInterface
  ): Observable<T & GenericResponseResult> {
    const traceId = crypto.randomUUID();
    const initBody = this._buildInitBody(init, traceId);
    if (!this.env.production) {
      this.amplitude.logEvent(
        'api call started',
        {
          body: JSON.stringify(initBody.body),
          type: 'del',
          url: `${this.env.awsmobile.endpoints['api-mobile'].endpoint}${path}`,
          trace_id: traceId,
        },
        true,
        100
      );
    }
    return from(this.awsAPIWrapper.del(apiName, path, initBody)).pipe(this._apiPipe<T>(apiName, path, initBody, 'del', options, traceId ));
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  _apiPipe<T>(apiName: string, path: string, init: any, callType: string, options?: APIOptionsInterface, traceId?: string) {
    return pipe(
      tap<T & GenericResponseResult>(()=>{
          if (this.simulateError && !this.env.production) {
            const simulatedError = new Error('Simulated server error') as any;
            simulatedError.status = 500;
            simulatedError.result = {
              status: 500,
              code: GenericResponseCode.SERVICE_ERROR,
              message: 'Simulated server error',
              suggestion: '',
              suggestionCopyKey: '',
              docs: ''
            };
            throw simulatedError;
          }
      }),
      retryWhen((errors) =>
        errors.pipe(
          delay(500),
          take(options?.retryCount ?? this.defaultRetryCount),
          concatMap((error) => throwError(error))
        )
      ),
      tap<T & GenericResponseResult>((response) => {
        const result = response.result;

        // catching status of 403 if the HttpResponse is still a 200
        if (result.status === 403 && result.message === this.awsAPIWrapper.unauthMsg) {
          this.awsAPIWrapper.forceLogout();
        }

        this.amplitude.logEvent(
          'api call completed',
          {
            reason_code: result.code,
            message: result.message,
            status: result.status,
            type: callType,
            body: this.env.production ? JSON.stringify({}) : JSON.stringify(init.body),
            url: `${this.env.awsmobile.endpoints['api-mobile'].endpoint}${path}`,
            trace_id: traceId,
          },
          true,
          100
        );
      }),
      catchError((error) => this._APIErrorHandler(apiName, path, init, error, callType, options))
    );
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  _APIErrorHandler = (
    apiName: string,
    path: string,
    init: any,
    error: unknown,
    callType: string,
    options?: APIOptionsInterface | undefined
  ): Observable<any> => {
    const errorAs = error as GenericResponseResult & HttpErrorResponse;

    if (path.includes('financialsnapshot')) {
      try {
        console.log('fs api call failed');
        this.amplitude.logEvent(
          'fs api call failed',
          {
            error: JSON.stringify(error),
            type: callType,
            url: `${this.env.awsmobile.endpoints['api-mobile'].endpoint}${path}`,
          },
          true,
          100
        );
      } catch (e) {}
    }

    // todo: update w/ core error logger when it is completed
    if (!this.env.production) console.log('APIErrorHandler: logging error');
    Sentry.withScope(function (scope: Scope) {
      scope.setFingerprint([`url: ${path}`, `request body: ${JSON.stringify(init)}`, `method: ${callType}`]);
      if (error instanceof Error) {
        Sentry.captureException(error);
      } else {
        try {
          // Convert or log non-Error objects
          const convertedError = new Error(JSON.stringify(error));
          Sentry.captureException(convertedError);
        } catch (er) {
          Sentry.captureException(er);
        }
      }
    });

    // unsure how the error is coming down from the service, catching for an HttpErrorResponse or our
    // own GenericResponseResult
    if (
      (errorAs.status && errorAs.status === 403 && errorAs.message === this.awsAPIWrapper.unauthMsg) ||
      (errorAs.result?.status === 403 && errorAs.result?.message === this.awsAPIWrapper.unauthMsg)
    ) {
      this.awsAPIWrapper.forceLogout();
    }

    if (options?.errorCallback) {
      options.errorCallback(error);
      return of([]);
    } else {
      return throwError(error);
    }
  };
}

@Injectable({
  providedIn: 'root',
})
export class BsApiService extends APIWrapperClass {

  private bsCacheService: BsCacheService = inject(BsCacheService);

  constructor(protected override amplitude: FirebaseService, protected override env: Environment) {
    const awsAPIWrapper: AwsApiWrapperService = inject(AwsApiWrapperService);

    super(amplitude, env, awsAPIWrapper);
  }

  get<T>(apiName: string, path: string, init?: any, options?: APIOptionsInterface): Observable<T & GenericResponseResult> {
    return this._apiGet<T>(apiName, path, init, options);
  }
  post<T>(apiName: string, path: string, init?: any, options?: APIOptionsInterface): Observable<T & GenericResponseResult> {
    return this._apiPost<T>(apiName, path, init, options);
  }
  patch<T>(apiName: string, path: string, init?: any, options?: APIOptionsInterface): Observable<T & GenericResponseResult> {
    return this._apiPatch<T>(apiName, path, init, options);
  }
  put<T>(apiName: string, path: string, init?: any, options?: APIOptionsInterface): Observable<T & GenericResponseResult> {
    return this._apiPut<T>(apiName, path, init, options);
  }
  del<T>(apiName: string, path: string, init?: any, options?: APIOptionsInterface): Observable<T & GenericResponseResult> {
    return this._apiDel<T>(apiName, path, init, options);
  }

  toggleService(): void {
    if (!this.env.production) {
      this.simulateError = !this.simulateError;
      console.log(`Simulated error toggled: ${this.simulateError}`);
    } else {
      console.warn('toggleService is disabled in production');
    }
  }
}
